/**
 * PresetPreview — rendert ein Preset-Layout als SVG
 * Exakt wie die OBS-Overlays: transparenter Hintergrund,
 * nur CAM-Labels in den Ecken (CAM weiß, Zahl blau)
 */

import React from 'react';
import './PresetPreview.css';

// Vorschau-Dimensionen (16:9)
const W = 160;
const H = 90;

// Label-Farben
const LABEL_WHITE = '#ffffff';
const LABEL_BLUE = '#1a9fff';
const LABEL_FONT = 'Impact, "Arial Black", sans-serif';
const LABEL_SIZE_MAIN = 11;   // Haupt-Cam Label
const LABEL_SIZE_SMALL = 9;   // Kleine Cam Label (Cam-in-Cam)
const LABEL_PADDING = 4;      // Abstand vom Rand

/**
 * Rendert ein CAM-Label: "CAM" weiß + Nummer blau
 * position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
 */
function CamLabel({ number, position, size = LABEL_SIZE_MAIN }) {
  const text = `CAM${number}`;
  const camPart = 'CAM';
  const numPart = String(number);

  // Geschätzte Zeichenbreite für Positionierung
  const charW = size * 0.62;
  const camW = camPart.length * charW;
  const numW = numPart.length * charW;
  const totalW = camW + numW;

  let x, y, anchor;

  if (position === 'top-left') {
    x = LABEL_PADDING;
    y = LABEL_PADDING + size;
    anchor = 'start';
  } else if (position === 'top-right') {
    x = W - LABEL_PADDING;
    y = LABEL_PADDING + size;
    anchor = 'end';
  } else if (position === 'bottom-left') {
    x = LABEL_PADDING;
    y = H - LABEL_PADDING;
    anchor = 'start';
  } else {
    x = W - LABEL_PADDING;
    y = H - LABEL_PADDING;
    anchor = 'end';
  }

  // Für "end"-Anchor: CAM-Teil zuerst, dann Zahl
  // Für "start"-Anchor: CAM-Teil zuerst, dann Zahl
  const xCam = anchor === 'end' ? x - numW : x;
  const xNum = anchor === 'end' ? x - numW : x + camW;

  return (
    <g style={{ filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.8))' }}>
      <text
        x={xCam}
        y={y}
        fontSize={size}
        fontFamily={LABEL_FONT}
        fontWeight="bold"
        fill={LABEL_WHITE}
        textAnchor="start"
        letterSpacing="0.5"
      >
        {camPart}
      </text>
      <text
        x={xNum}
        y={y}
        fontSize={size}
        fontFamily={LABEL_FONT}
        fontWeight="bold"
        fill={LABEL_BLUE}
        textAnchor="start"
        letterSpacing="0.5"
      >
        {numPart}
      </text>
    </g>
  );
}

/**
 * Only Cam — eine Cam fullscreen, Label oben links
 */
function OnlyCamPreview({ camNumber }) {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="preset-preview__svg">
      {/* Dunkler Hintergrund simuliert Kamerabild */}
      <rect width={W} height={H} fill="#111" rx="2" />
      <CamLabel number={camNumber} position="top-left" size={LABEL_SIZE_MAIN} />
    </svg>
  );
}

/**
 * Cam in Cam — Haupt-Cam fullscreen + kleine Cam in Ecke
 * mainCam: Nummer der Haupt-Cam (oben rechts)
 * smallCam: Nummer der kleinen Cam
 * smallPos: 'left' | 'right' (Seite der kleinen Cam)
 */
function CamInCamPreview({ mainCam, smallCam, smallPos }) {
  // Kleine Cam: ca. 25% der Breite, 16:9
  const smallW = W * 0.27;
  const smallH = smallW * (9 / 16);
  const margin = 3;

  const smallX = smallPos === 'left' ? margin : W - smallW - margin;
  const smallY = H - smallH - margin;

  // Label-Position für kleine Cam
  const smallLabelPos = smallPos === 'left' ? 'top-left' : 'top-right';

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="preset-preview__svg">
      {/* Haupt-Cam Hintergrund */}
      <rect width={W} height={H} fill="#111" rx="2" />

      {/* Kleine Cam — Schatten */}
      <rect
        x={smallX + 1.5}
        y={smallY + 1.5}
        width={smallW}
        height={smallH}
        fill="rgba(0,0,0,0.6)"
        rx="1"
      />
      {/* Kleine Cam — Fläche */}
      <rect
        x={smallX}
        y={smallY}
        width={smallW}
        height={smallH}
        fill="#1a1a1a"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="0.5"
        rx="1"
      />

      {/* Haupt-Cam Label oben rechts */}
      <CamLabel number={mainCam} position="top-right" size={LABEL_SIZE_MAIN} />

      {/* Kleine Cam Label — oben links/rechts der kleinen Cam */}
      <g transform={`translate(${smallX}, ${smallY})`}>
        <svg width={smallW} height={smallH} viewBox={`0 0 ${W} ${H}`} overflow="visible">
          <CamLabel
            number={smallCam}
            position={smallPos === 'left' ? 'top-left' : 'top-right'}
            size={LABEL_SIZE_MAIN}
          />
        </svg>
      </g>
    </svg>
  );
}

/**
 * Cam + Cam — 2 oder 3 Cams nebeneinander
 */
function CamPlusCamPreview({ cams }) {
  // cams: Array von Cam-Nummern von links nach rechts
  const count = cams.length;
  const camW = W / count;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="preset-preview__svg">
      {cams.map((camNum, i) => (
        <g key={camNum}>
          {/* Cam-Fläche */}
          <rect
            x={i * camW + (i > 0 ? 0.5 : 0)}
            y={0}
            width={camW - (i > 0 ? 0.5 : 0)}
            height={H}
            fill={i % 2 === 0 ? '#111' : '#161616'}
            rx={i === 0 ? '2 0 0 2' : i === count - 1 ? '0 2 2 0' : '0'}
          />
          {/* Trennlinie */}
          {i > 0 && (
            <line
              x1={i * camW}
              y1={0}
              x2={i * camW}
              y2={H}
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="0.5"
            />
          )}
          {/* Label oben links in jeder Cam */}
          <g transform={`translate(${i * camW}, 0)`}>
            <svg width={camW} height={H} viewBox={`0 0 ${W} ${H}`} overflow="visible">
              <CamLabel number={camNum} position="top-left" size={LABEL_SIZE_MAIN} />
            </svg>
          </g>
        </g>
      ))}
    </svg>
  );
}

/**
 * Pause-Vorschau
 */
function PausePreview() {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="preset-preview__svg">
      <rect width={W} height={H} fill="#111" rx="2" />
      <rect x={58} y={28} width={10} height={34} fill="#7c3aed" rx="1.5" opacity="0.9" />
      <rect x={74} y={28} width={10} height={34} fill="#7c3aed" rx="1.5" opacity="0.9" />
      <text x={W/2} y={H-8} textAnchor="middle" fontSize="6" fill="rgba(255,255,255,0.4)" fontFamily="sans-serif" letterSpacing="2">PAUSE</text>
    </svg>
  );
}

/**
 * Einstellungen-Vorschau
 */
function SettingsPreview() {
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="preset-preview__svg">
      <rect width={W} height={H} fill="#111" rx="2" />
      <circle cx={W/2} cy={H/2-4} r={13} fill="none" stroke="#7c3aed" strokeWidth="2.5" opacity="0.9" />
      <circle cx={W/2} cy={H/2-4} r={5} fill="#7c3aed" opacity="0.9" />
      <text x={W/2} y={H-8} textAnchor="middle" fontSize="5" fill="rgba(255,255,255,0.4)" fontFamily="sans-serif" letterSpacing="1">EINSTELLUNGEN</text>
    </svg>
  );
}

/**
 * Parst den Preset-Namen und gibt die richtige Vorschau zurück
 */
function parsePreset(preset) {
  const name = preset.name;

  // Only Cam
  if (name.startsWith('Only Cam ')) {
    const num = parseInt(name.replace('Only Cam ', ''));
    return <OnlyCamPreview camNumber={num} />;
  }

  // Cam in Cam — z.B. "Cam in Cam 1+2l" oder "Cam in Cam 1+2r"
  if (name.startsWith('Cam in Cam ')) {
    const rest = name.replace('Cam in Cam ', '');
    const side = rest.endsWith('l') ? 'left' : 'right';
    const nums = rest.replace(/[lr]$/, '').split('+').map(Number);
    return <CamInCamPreview mainCam={nums[0]} smallCam={nums[1]} smallPos={side} />;
  }

  // Cam + Cam — z.B. "Cam+Cam 2+1" oder "Cam+Cam 3+2+1"
  if (name.startsWith('Cam+Cam ')) {
    const nums = name.replace('Cam+Cam ', '').split('+').map(Number);
    // Reihenfolge: von links nach rechts (höchste Zahl links)
    return <CamPlusCamPreview cams={nums} />;
  }

  // Szenen-Presets
  if (name === 'Pause') return <PausePreview />;
  if (name === 'Einstellungen') return <SettingsPreview />;

  // Fallback
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="preset-preview__svg">
      <rect width={W} height={H} fill="#111" rx="2" />
      <text x={W/2} y={H/2+3} textAnchor="middle" fontSize="7" fill="#aaa" fontFamily="sans-serif">{name}</text>
    </svg>
  );
}

export default function PresetPreview({ preset }) {
  if (!preset) return null;
  return parsePreset(preset);
}
